import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Subject, combineLatest, Observable, BehaviorSubject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { ErrorSinkService, handleModbusError, ToastService, UiProgressPromiseService, UiPromiseService } from 'ngx-zcs-components';
import { splitHighLow, mergeHighLow, CommunicationChannel, CommunicationChannelType, TypeInternetDevice } from 'ngx-modbus';
import { TranslateService } from '@ngx-translate/core';
import { ProductModelService, InverterModelType, getInverterType, InsertDeviceCommandLogMutation } from 'ngx-core-services';
import {
  DeviceModbusInfo,
  InverterSettingsCodeV2,
  InverterSettingsCodeV3,
  InverterSettingsCodeV4,
  V3AntiRefluxSettings,
  V3BatteryActiveSettings,
  V3BatterySettings,
  V3DeviceAntiRefluxAndPCCSettings,
  V3DevicePCCSettings,
  V3DeviceWorkModeConfig,
  V3DeviceWorkModePassive,
  V3DeviceWorkModeTOU,
  V3DeviceWorkModeTiming,
  V3EpsSettings,
  V3GlobalSettings,
  V3IVCurveScanSettings,
  V3InputModeChannels,
  V3ModbusSettings,
  V3ParallelSettings,
  V3PurchasePowerLimitSettings,
  V3UnbalancedSupportSettings,
} from 'ngx-modbus';
import { CommonModule } from '@angular/common';
import gql from 'graphql-tag';
import * as Sentry from "@sentry/angular";

import { ChannelHandler, ModbusReadService } from '../../communication';
import { AccordionInfoComponent } from './components/accordion-info/accordion-info.component';
import { GlobalSetupComponent } from './components/global-setup/global-setup.component';
import { DateTimeSelectorComponent } from './components/date-time-selector/date-time-selector.component';
import { WeekdaySelectorComponent } from './components/weekday-selector/weekday-selector.component';
import { ConfigInputTypeChannelComponent } from './components/config-input-type-channel/config-input-type-channel.component';

const getLabels = () => ({
  "DeviceSettingsPage.batteryConfiguration": "Battery Configuration",
  "DeviceSettingsPage.batteryConfigID": "Battery Pack Config ID - {{ID}}",
  "Common.change": "Change",
  "DeviceSettingsPage.batteryCommunicationProtocol": "Battery Communication Protocol",
  "DeviceSettingsPage.batteryCommunicationAddress": "Battery Communication Address - {{address}}",
  "Common.save": "Save",
  "DeviceSettingsPage.batteryCurrentChargeLimit": "Battery Current Charge Limit - {{limit}}A",
  "DeviceSettingsPage.batteryCurrentDischargeLimit": "Battery Current Discharge Limit - {{limit}}A",
  "DeviceSettingsPage.batteryDepthOfDischarge": "Battery Depth of Discharge - {{Discharge}}%",
  "DeviceSettingsPage.batteryEndOfDischarge": "EPS Battery Discharge Depth - {{Discharge}}%",
  "DeviceSettingsPage.modbusConfiguration": "Modbus Configuration",
  "DeviceSettingsPage.rS485ConfigAddress": "RS485 Config Address - {{address}}",
  "DeviceSettingsPage.rS485BaudRateSelection": "RS485 Baud Rate Selection - {{configBaud}}",
  "DeviceSettingsPage.rS485StopBitSelection": "RS485 Stop Bit Selection - {{configStopBit}}",
  "DeviceSettingsPage.rS485ParityBitSelection": "RS485 Parity Bit Selection - {{configParityBit}}",
  "DeviceSettingsPage.noCheckNone": "No Check/None (Default)",
  "DeviceSettingsPage.even": "Even",
  "DeviceSettingsPage.odd": "Odd",
  "DeviceSettingsPage.highMark": "High/Mark",
  "DeviceSettingsPage.lowSpace": "Even",
  "DeviceSettingsPage.IVCurveScanSettings": "IV Curve Scan Settings",
  "DeviceSettingsPage.IVCurveScanControl": "IV Curve Scan Control",
  "Common.disable": "Disable",
  "Common.enable": "Enable",
  "DeviceSettingsPage.IVCurveScanPeriod": "IV Curve Scan Period - {{period}} min",
  "DeviceSettingsPage.antiRefluxSettings": "Zero Emission Settings",
  "DeviceSettingsPage.disableRefluxPower": "Disable reflux power",
  "DeviceSettingsPage.powerRefluxEnableWithMinimumPower": "Zero Emission",
  "DeviceSettingsPage.powerRefluxEnableWithMeaningValue": "Lim.three-phase",
  "Common.doNoUse": "Do not use",
  "DeviceInfoPage.electricityMeter": "Electricity Meter",
  "DeviceInfoPage.ct": "CT",
  "DeviceInfoPage.aRPC": "ARPC",
  "DeviceSettingsPage.antiRefluxControl": "Zero Emission Control",
  "DeviceSettingsPage.refluxPower": "Reflux Power",
  "DeviceSettingsPage.PCCSampleMode": "PCC Sample Mode",
  "DeviceSettingsPage.ePSSettings": "EPS Settings",
  "DeviceSettingsPage.ePSControl": "EPS Control",
  "DeviceSettingsPage.disableEmergencyPowerSupply": "disabled",
  "DeviceSettingsPage.enableEmergencyPowerSupplyEnableColdStart": "Enabled",
  "DeviceSettingsPage.batteryActiveSettings": "Battery Active Settings",
  "DeviceSettingsPage.batteryActiveControl": "Battery Active Control",
  "DeviceSettingsPage.batteryActiveOneShot": "Battery Active One Shot",
  "Common.trigger": "Trigger",
  "Common.forceNow": "Force now",
  "DeviceSettingsPage.unbalancedSupportSettings": "Unbalanced Support Settings",
  "DeviceSettingsPage.unbalancedSupportControl": "Unbalanced Support Control",
  "DeviceSettingsPage.parallelSettings": "Parallel Settings",
  "DeviceSettingsPage.parallelControl": "Parallel Control",
  "DeviceSettingsPage.parallelMasterSlave": "Parallel Master/Slave",
  "DeviceSettingsPage.parallelAddress": "Parallel Address",
  "DeviceSettingsPage.inputModeSettings": "Input Mode Settings",
  "DeviceSettingsPage.workModeSettings": "Work Mode Settings",
  "DeviceSettingsPage.energyStorageModeControl": "Work Mode",
  "DeviceSettingsPage.spontaneousSelfUseMode": "Automatic mode",
  "DeviceSettingsPage.timeOfUseElectricityPriceModel": "Time-of-use electricity price model",
  "DeviceSettingsPage.timingChargingAndDischargingMode": "Timing charging and discharging mode",
  "DeviceSettingsPage.passiveMode": "Passive mode",
  "DeviceSettingsPage.peakClippingMode": "Peak clipping mode",
  "DeviceSettingsPage.offGridMode": "Off-grid mode",
  "DeviceSettingsPage.generatorMode": "Generator mode",
  "DeviceSettingsPage.timingID": "Rule id",
  "DeviceSettingsPage.timingOffOnControl": "Timing Off On Control",
  "DeviceSettingsPage.disabledDischargingAndCharging": "Disabled discharging and charging",
  "DeviceSettingsPage.enabledOnlyCharging": "Enabled only charging",
  "DeviceSettingsPage.enabledOnlyDischarging": "Enabled only discharging",
  "DeviceSettingsPage.enabledDischargingAndCharging": "Enabled discharging and charging",
  "DeviceSettingsPage.timingChargeStart": "Timing Charge Start",
  "DeviceSettingsPage.timingChargeEnd": "Timing Charge End",
  "DeviceSettingsPage.timingDischargeStart": "Timing Discharge Start",
  "DeviceSettingsPage.timingDischargeEnd": "Timing Discharge End",
  "DeviceSettingsPage.timingChargePower": "Timing Charge Power",
  "DeviceSettingsPage.timingDischargePower": "Timing Discharge Power",
  "DeviceSettingsPage.timeOfUseElectricityPriceRuleNumber": "Time-of-use electricity price-rule number",
  "DeviceSettingsPage.timeOfUsePriceRuleEnable": "Time-of-use price-rule enable",
  "DeviceSettingsPage.chargingStartHourAndMinute": "Charging start hour and minute",
  "DeviceSettingsPage.chargingEndHourAndMinute": "Charging end hour and minute",
  "DeviceSettingsPage.timeOfUsePriceMandatoryChargingCutOffSOC": "Time-of-use price-mandatory charging cut-off SOC",
  "DeviceSettingsPage.timeOfUsePriceMandatoryChargingPower": "Time-of-use price-mandatory charging power",
  "DeviceSettingsPage.theMonthAndDateWhenTheRuleBecomesEffective": "The month and date when the rule becomes effective",
  "DeviceSettingsPage.theMonthAndDateWhenTheRuleCeasesToTakeEffect": "The month and date when the rule ceases to take effect",
  "DeviceSettingsPage.timeOfUseElectricityPriceWeekWhenTheRuleTakesEffect": "Time-of-use electricity price-week when the rule takes effect",
  "DeviceSettingsPage.batteryPowerLowerLimit": "Battery Power Lower Limit",
  "DeviceSettingsPage.batteryPowerUpperLimit": "Battery Power Upper Limit",
  "DeviceSettingsPage.desireGridPower": "Desire Grid Power",
  "Common.saving": "Saving...",
  "Common.saveFailed": "Save failed",
  "DeviceSettingsPage.purchasePowerLimitSettings": "Import Power Limit Settings",
  "DeviceSettingsPage.purchasePowerLimitControl": "Purchase Power Limit Control",
  "DeviceSettingsPage.purchasePowerLimitValue": "Purchase Power Limit (W)",
})

const BTS_5K_PROTOCOLS = [11];
const calculateBatteryConfigAddress = (value: any, configId: any, protocolId: any) => {
  if ([32, 16].includes(value)) {
    return 'Not used';
  }
  if (configId === 1 && BTS_5K_PROTOCOLS.includes(protocolId)) {
    const result = (value as number) - 16;
    if (result < 0) {
      return 'Misconfigured';
    }
    return result;
  }
  return value;
};

const setBatteryConfigAddress = (value: any, configId: any, protocolId: any) => {
  if (configId === 1 && BTS_5K_PROTOCOLS.includes(protocolId)) {
    return value + 16;
  }
  return value;
};

@Component({
  selector: 'lib-settings-inverter',
  standalone: true,
  imports: [
    AccordionInfoComponent,
    CommonModule,
    GlobalSetupComponent,
    DateTimeSelectorComponent,
    WeekdaySelectorComponent,
    ConfigInputTypeChannelComponent,
  ],
  templateUrl: './settings-inverter.component.html',
  styleUrls: ['./settings-inverter.component.scss'],
})
export class SettingsInverterComponent implements OnInit, OnDestroy {
  @ViewChild('dateSelector') dateSelector?: TemplateRef<any>;
  @ViewChild('globalSetup') content?: TemplateRef<any>;
  @ViewChild('inputChannelSelector') inputChannelSelector?: TemplateRef<any>;
  @ViewChild('timeSelector') timeSelector?: TemplateRef<any>;
  @ViewChild('weekdaySelector') weekdaySelector?: TemplateRef<any>;

  @Input() inverterId?: string;
  @Input() channelType?: CommunicationChannelType;
  @Input() gatewaySN?: string;
  @Input() modbusAddress?: number;
  @Output() fatalError = new EventEmitter<void>();

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): ReturnType <typeof getLabels> {
    return { ...this._labels };
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';

  isCommunicationReady$ = new BehaviorSubject<boolean>(false);
  cHandler = this.channelHandler.handler;
  command = this.cHandler.channel.command;

  readonly refresh$ = new Subject<void>();

  readonly getInverterModelType = () => getInverterType(this.cHandler.channel.inverterId);
  readonly InverterModelType = InverterModelType;

  /**
   * Proxy that always resolves to the current channel (via this.cHandler).
   * Field declarations capture `this.cHandler.channel` at construction time,
   * but ngOnInit may switch to a different channel via channelHandler.switch().
   * This proxy ensures read$() always uses the channel that was actually opened.
   */
  private readonly channelProxy = new Proxy({} as CommunicationChannel, {
    get: (_target, prop, receiver) => {
      const currentChannel = this.cHandler.channel;
      const value = Reflect.get(currentChannel, prop, receiver);
      return typeof value === 'function' ? value.bind(currentChannel) : value;
    },
  });

  private readonly refreshedRead$ = (table: any, composer?: any, opts?: any, label = 'settings') =>
    this.modbusRead.read$(this.channelProxy, this.refresh$, table, composer, opts).pipe(
      handleModbusError(this.errorSink, label),
    );

  readonly globalConfig$ = this.refreshedRead$(V3GlobalSettings, undefined, undefined, 'global');
  readonly batteryConfig$ = this.refreshedRead$(V3BatterySettings, {}, {
    maxRegisterPerPage: 0x20, useValidityMask: 'AddressMask_Config_Basic2',
  }, 'battery-config');
  readonly inputModeConfig$ = this.refreshedRead$(V3InputModeChannels, {}, {
    maxRegisterPerPage: 0x20,
  }, 'input-mode');
  readonly antiRefluxConfig$ = this.refreshedRead$(V3DeviceAntiRefluxAndPCCSettings, undefined, undefined, 'anti-reflux');
  readonly ppcSampleModeConfig$ = this.refreshedRead$(V3DevicePCCSettings, undefined, undefined, 'pcc-sample');
  readonly modbusConfig$ = this.refreshedRead$(V3ModbusSettings, undefined, undefined, 'modbus');
  readonly ivCurveScanConfig$ = this.refreshedRead$(V3IVCurveScanSettings, undefined, undefined, 'iv-curve');
  readonly epsConfig$ = this.refreshedRead$(V3EpsSettings, undefined, undefined, 'eps');
  readonly batteryActiveConfig$ = this.refreshedRead$(V3BatteryActiveSettings, undefined, undefined, 'battery-active');
  readonly unbalancedSupportConfig$ = this.refreshedRead$(V3UnbalancedSupportSettings, undefined, undefined, 'unbalanced');
  readonly parallelConfig$ = this.refreshedRead$(V3ParallelSettings, undefined, undefined, 'parallel');
  readonly purchasePowerLimitConfig$ = this.refreshedRead$(
    V3PurchasePowerLimitSettings, {}, { optional: true }, 'purchase-power-limit',
  );
  readonly inputWorkModeInfo$ = this.refreshedRead$(V3DeviceWorkModeConfig, {}, {
    maxRegisterPerPage: 0x40, useValidityMask: 'AddressMask_Config_Remote1',
  }, 'work-mode');

  readonly settings$: Observable<DeviceModbusInfo[]> = combineLatest([
    this.isCommunicationReady$.pipe(filter((ready) => ready)),
    this.refresh$.pipe(startWith(null)),
  ]).pipe(
    map((_) => {
      const batteryConfig = {
        [this.labels['DeviceSettingsPage.batteryConfiguration']]: {
          flow: {
            type: 'component' as const,
            value$: this.batteryConfig$.pipe(
              map((data) => ([
                {
                  name: `${this.labels['DeviceSettingsPage.batteryConfigID']} - ${data?.BAT_CONFIG_ID}`,
                  key: 'BAT_CONFIG_ID',
                  code: InverterSettingsCodeV3.BatteryConfigID,
                  order: 0,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_ID === false,
                    value: data?.BAT_CONFIG_ID,
                    constraint: { options: Array(2).fill(null).map((__, i) => ({ label: `Battery pack - ${i + 1}`, value: i })) },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.batteryCommunicationProtocol'],
                  key: 'BAT_CONFIG_PROTOCOL',
                  code: InverterSettingsCodeV3.BatteryCommunicationProtocol,
                  order: 1,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_PROTOCOL === false,
                    value: data?.BAT_CONFIG_PROTOCOL,
                    constraint: {
                      options: [
                        { label: 'Sofar Built-in BMS', value: 0 },
                        { label: 'Pylontech', value: 1 },
                        { label: 'Weco LV', value: 2 },
                        { label: 'ZSX Azzurro', value: 3 },
                        { label: 'LG', value: 4 },
                        { label: 'Alpha ESS', value: 5 },
                        { label: 'CATL', value: 6 },
                        { label: 'Weco HV', value: 7 },
                        { label: 'BTS 5K', value: 11 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryCommunicationAddress']} - Bat#1`,
                  key: 'BAT_CONFIG_ADDRESS_1',
                  code: InverterSettingsCodeV3.BatteryCommunicationAddress,
                  order: 2,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_ADDRESS_1 === false,
                    value: calculateBatteryConfigAddress(data?.BAT_CONFIG_ADDRESS_1, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL),
                    constraint: {
                      options: (() => {
                        const options = [];
                        const value = calculateBatteryConfigAddress(
                          data?.BAT_CONFIG_ADDRESS_1, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL,
                        );

                        if (BTS_5K_PROTOCOLS.includes(data?.BAT_CONFIG_PROTOCOL as number)) {
                          options.push({ label: 'Not used', value: 32 });
                        }

                        if (value !== 1 && value !== 0) {
                          options.push({ label: `${value}`, value });
                        }

                        options.push({ label: '0', value: 0 });
                        options.push({ label: '1', value: 1 });

                        return options;
                      })(),
                    },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryCommunicationAddress']} - Bat#2`,
                  key: 'BAT_CONFIG_ADDRESS_2',
                  code: InverterSettingsCodeV3.BatteryCommunicationAddress,
                  order: 2,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_ADDRESS_2 === false,
                    value: calculateBatteryConfigAddress(data?.BAT_CONFIG_ADDRESS_2, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL),
                    constraint: {
                      options: (() => {
                        const options = [];
                        const value = calculateBatteryConfigAddress(
                          data?.BAT_CONFIG_ADDRESS_2, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL,
                        );

                        if (BTS_5K_PROTOCOLS.includes(data?.BAT_CONFIG_PROTOCOL as number)) {
                          options.push({ label: 'Not used', value: 32 });
                        }

                        if (value !== 2) {
                          options.push({ label: `${value}`, value });
                        }

                        options.push({ label: '2', value: 2 });

                        return options;
                      })(),
                    },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryCommunicationAddress']} - Bat#3`,
                  key: 'BAT_CONFIG_ADDRESS_3',
                  code: InverterSettingsCodeV3.BatteryCommunicationAddress,
                  order: 2,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_ADDRESS_3 === false,
                    value: calculateBatteryConfigAddress(data?.BAT_CONFIG_ADDRESS_3, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL),
                    constraint: {
                      options: (() => {
                        const options = [];
                        const value = calculateBatteryConfigAddress(
                          data?.BAT_CONFIG_ADDRESS_3, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL,
                        );

                        if (BTS_5K_PROTOCOLS.includes(data?.BAT_CONFIG_PROTOCOL as number)) {
                          options.push({ label: 'Not used', value: 32 });
                        }

                        if (value !== 3) {
                          options.push({ label: `${value}`, value });
                        }

                        options.push({ label: '3', value: 3 });

                        return options;
                      })(),
                    },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryCommunicationAddress']} - Bat#4`,
                  key: 'BAT_CONFIG_ADDRESS_4',
                  code: InverterSettingsCodeV3.BatteryCommunicationAddress,
                  order: 2,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_ADDRESS_4 === false,
                    value: calculateBatteryConfigAddress(data?.BAT_CONFIG_ADDRESS_4, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL),
                    constraint: {
                      options: (() => {
                        const options = [];
                        const value = calculateBatteryConfigAddress(
                          data?.BAT_CONFIG_ADDRESS_4, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL,
                        );

                        if (BTS_5K_PROTOCOLS.includes(data?.BAT_CONFIG_PROTOCOL as number)) {
                          options.push({ label: 'Not used', value: 32 });
                        }

                        if (value !== 4) {
                          options.push({ label: `${value}`, value });
                        }

                        options.push({ label: '4', value: 4 });

                        return options;
                      })(),
                    },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryCurrentChargeLimit']} - ${data?.BAT_CONFIG_CURRENT_CHARGE_LIMIT}A`,
                  key: 'BAT_CONFIG_CURRENT_CHARGE_LIMIT',
                  code: InverterSettingsCodeV3.BatteryCurrentChargeLimit,
                  order: 3,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_CURRENT_CHARGE_LIMIT === false,
                    value: data?.BAT_CONFIG_CURRENT_CHARGE_LIMIT,
                    constraint: { min: 0, max: 65535 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryCurrentDischargeLimit']} - ${data?.BAT_CONFIG_CURRENT_DISCHARGE_LIMIT}A`,
                  key: 'BAT_CONFIG_CURRENT_DISCHARGE_LIMIT',
                  code: InverterSettingsCodeV3.BatteryCurrentDischargeLimit,
                  order: 4,
                  field: {
                    hide: this.command?.validity.BAT_CONFIG_CURRENT_DISCHARGE_LIMIT === false,
                    value: data?.BAT_CONFIG_CURRENT_DISCHARGE_LIMIT,
                    constraint: { min: 0, max: 65535 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryDepthOfDischarge']} - ${data?.BAT_DEPTH_OF_DISCHARGE}%`,
                  key: 'BAT_DEPTH_OF_DISCHARGE',
                  code: InverterSettingsCodeV3.BatteryDepthOfDischarge,
                  order: 5,
                  field: {
                    hide: this.command?.validity.BAT_DEPTH_OF_DISCHARGE === false,
                    value: data?.BAT_DEPTH_OF_DISCHARGE,
                    constraint: { min: 10, max: 90 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.batteryEndOfDischarge']} - ${data?.BAT_END_OF_DISCHARGE}%`,
                  key: 'BAT_END_OF_DISCHARGE',
                  code: InverterSettingsCodeV3.BatteryEndOfDischarge,
                  order: 6,
                  field: {
                    hide: this.command?.validity.BAT_END_OF_DISCHARGE === false,
                    value: data?.BAT_END_OF_DISCHARGE,
                    constraint: { min: 10, max: 90 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data: data.sort((a, b) => a.order - b.order) },
              })),
            ),
          },
        },
      };

      const modbusConfig = {
        [this.labels['DeviceSettingsPage.modbusConfiguration']]: {
          flow: {
            type: 'component' as const,
            value$: this.modbusConfig$.pipe(
              map((data) => ([
                {
                  name: `${this.labels['DeviceSettingsPage.rS485ConfigAddress']} - ${data?.RS485Config_Address}`,
                  key: 'RS485Config_Address',
                  code: InverterSettingsCodeV3.RS485ConfigAddress,
                  order: 0,
                  field: {
                    hide: this.command?.validity.RS485Config_Address === false,
                    value: data?.RS485Config_Address,
                    constraint: { min: 1, max: 247 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.rS485BaudRateSelection']} - ${data?.RS485Config_Baud}`,
                  key: 'RS485Config_Baud',
                  code: InverterSettingsCodeV3.RS485ConfigBaud,
                  order: 1,
                  field: {
                    hide: this.command?.validity.RS485Config_Baud === false,
                    value: data?.RS485Config_Baud,
                    constraint: {
                      options: [
                        { label: '4800bps', value: 0 },
                        { label: '9600bps (Default)', value: 1 },
                        { label: '19200bps', value: 2 },
                        { label: '38400bps', value: 3 },
                        { label: '57600bps', value: 4 },
                        { label: '115200bps', value: 5 },
                        { label: '1200bps', value: 10 },
                        { label: '2400bps', value: 11 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.rS485StopBitSelection']} - ${data?.RS485Config_StopBit}`,
                  key: 'RS485Config_StopBit',
                  code: InverterSettingsCodeV3.RS485ConfigStopBit,
                  order: 2,
                  field: {
                    hide: this.command?.validity.RS485Config_StopBit === false,
                    value: data?.RS485Config_StopBit,
                    constraint: {
                      options: [
                        { label: '1 bit stop bit (Default)', value: 0 },
                        { label: '1.5 bit stop bit', value: 1 },
                        { label: '2 bit stop bit', value: 2 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.rS485ParityBitSelection']} - ${data?.RS485Config_ParityBit}`,
                  key: 'RS485Config_ParityBit',
                  code: InverterSettingsCodeV3.RS485ConfigParityBit,
                  order: 3,
                  field: {
                    hide: this.command?.validity.RS485Config_ParityBit === false,
                    value: data?.RS485Config_ParityBit,
                    constraint: {
                      options: [
                        { label: this.labels['DeviceSettingsPage.noCheckNone'], value: 0 },
                        { label: this.labels['DeviceSettingsPage.even'], value: 1 },
                        { label: this.labels['DeviceSettingsPage.odd'], value: 2 },
                        { label: this.labels['DeviceSettingsPage.highMark'], value: 3 },
                        { label: this.labels['DeviceSettingsPage.lowSpace'], value: 4 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data: data.sort((a, b) => a.order - b.order) },
              })),
            ),
          },
        },
      };

      const ivCurveScanSettings = {
        [this.labels['DeviceSettingsPage.IVCurveScanSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.ivCurveScanConfig$.pipe(
              map((data) => ([
                {
                  name: this.labels['DeviceSettingsPage.IVCurveScanControl'],
                  key: 'IVCurveScan_Control',
                  code: InverterSettingsCodeV3.IVCurveScanControl,
                  field: {
                    hide: this.command?.validity.IVCurveScan_Control === false,
                    value: data?.IVCurveScan_Control,
                    constraint: { options: [{
                      label: this.labels['Common.disable'],
                      value: 0,
                    }, {
                      label: this.labels['Common.enable'],
                      value: 1,
                    }] },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: `${this.labels['DeviceSettingsPage.IVCurveScanPeriod']} - ${data?.IVCurveScan_Period} min`,
                  key: 'IVCurveScan_Period',
                  code: InverterSettingsCodeV3.IVCurveScanPeriod,
                  field: {
                    hide: this.command?.validity.IVCurveScan_Period === false,
                    value: data?.IVCurveScan_Period,
                    constraint: { min: 5, max: 65535 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const antiRefluxSettings = {
        [this.labels['DeviceSettingsPage.antiRefluxSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.antiRefluxConfig$.pipe(
              map((data) => {
                const antiRefluxControlOptions = [{
                  label: this.labels['DeviceSettingsPage.disableRefluxPower'],
                  value: 0,
                }, {
                  label: this.labels['DeviceSettingsPage.powerRefluxEnableWithMinimumPower'],
                  value: 1,
                }];

                const product = new ProductModelService(this.inverterId || '');
                if (product.type?.name === 'inverter' && product.type.phase === 'three-phase') {
                  antiRefluxControlOptions.push({
                    label: this.labels['DeviceSettingsPage.powerRefluxEnableWithMeaningValue'],
                    value: 2,
                  });
                }

                const PccSampleModeOptions = [{
                  label: this.labels['Common.doNoUse'],
                  value: 0,
                }, {
                  label: this.labels['DeviceInfoPage.electricityMeter'],
                  value: 1,
                }, {
                  label: this.labels['DeviceInfoPage.ct'],
                  value: 2,
                }, {
                  label: this.labels['DeviceInfoPage.aRPC'],
                  value: 3,
                }];
                return [
                  {
                    name: this.labels['DeviceSettingsPage.antiRefluxControl'],
                    key: 'AntiReflux_Control',
                    code: InverterSettingsCodeV3.AntiRefluxControl,
                    field: {
                      hide: this.command?.validity.AntiReflux_Control === false,
                      value: data?.AntiReflux_Control,
                      constraint: { options: antiRefluxControlOptions },
                      type: 'select',
                      submit: this.labels['Common.change'],
                    },
                  },
                  {
                    name: this.labels['DeviceSettingsPage.refluxPower'],
                    key: 'AntiReflux_Power',
                    code: InverterSettingsCodeV3.AntiRefluxPower,
                    field: {
                      hide: data?.AntiReflux_Control === 0 || this.command?.validity.AntiReflux_Power === false,
                      value: data?.AntiReflux_Power,
                      constraint: { min: 0, max: 65535 },
                      type: 'input',
                      inputType: 'number',
                      submit: this.labels['Common.save'],
                    },
                  },
                  {
                    name: this.labels['DeviceSettingsPage.PCCSampleMode'],
                    key: 'PCCSampleMode',
                    code: InverterSettingsCodeV3.PCCSampleMode,
                    field: {
                      hide: this.command?.validity.PCCSampleMode === false,
                      value: data?.PCCSampleMode,
                      constraint: { options: PccSampleModeOptions },
                      type: 'select',
                      submit: this.labels['Common.change'],
                    },
                  },
                ];
              }),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const epsSettings = {
        [this.labels['DeviceSettingsPage.ePSSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.epsConfig$.pipe(
              map((data) => ([
                {
                  name: this.labels['DeviceSettingsPage.ePSControl'],
                  key: 'EPS_Control',
                  code: InverterSettingsCodeV3.EpsControl,
                  field: {
                    hide: this.command?.validity.EPS_Control === false,
                    value: data?.EPS_Control,
                    constraint: { options: [{
                      label: this.labels['DeviceSettingsPage.disableEmergencyPowerSupply'],
                      value: 0,
                    }, {
                      label: this.labels['DeviceSettingsPage.enableEmergencyPowerSupplyEnableColdStart'],
                    value: 2,
                    }]},
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const batteryActiveConfig = {
        [this.labels['DeviceSettingsPage.batteryActiveSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.batteryActiveConfig$.pipe(
              map((data) => ([
                {
                  name: this.labels['DeviceSettingsPage.batteryActiveControl'],
                  key: 'BatteryActive_Control',
                  code: InverterSettingsCodeV3.BatteryActiveControl,
                  field: {
                    hide: this.command?.validity.BatteryActive_Control === false,
                    value: data?.BatteryActive_Control,
                    constraint: {
                      options: [
                        { label: this.labels['Common.disable'], value: 0 },
                        { label: this.labels['Common.enable'], value: 1 },
                      ]},
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                }, {
                  name: this.labels['DeviceSettingsPage.batteryActiveOneShot'],
                  key: 'BatteryActive_Oneshot',
                  code: InverterSettingsCodeV3.BatteryActiveOneShot,
                  field: {
                    hide: this.command?.data.BatteryActive_OneShot === 0,
                    value: 1,
                    type: 'trigger',
                    submit: this.labels['Common.trigger'],
                    constraint: {
                      label: this.labels['Common.forceNow'],
                    },
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const unbalancedSupportConfig = {
        [this.labels['DeviceSettingsPage.unbalancedSupportSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.unbalancedSupportConfig$.pipe(
              map((data) => ([
                {
                  name: this.labels['DeviceSettingsPage.unbalancedSupportControl'],
                  key: 'UnbalancedSupport_Control',
                  code: InverterSettingsCodeV3.UnbalancedSupportControl,
                  field: {
                    hide: this.command?.validity.UnbalancedSupport_Control === false,
                    value: data?.UnbalancedSupport_Control,
                    constraint: {
                      options: [
                        { label: this.labels['Common.disable'], value: 0 },
                        { label: this.labels['Common.enable'], value: 1 },
                      ]},
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const parallelConfig = {
        [this.labels['DeviceSettingsPage.parallelSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.parallelConfig$.pipe(
              map((data) => ([
                {
                  name: this.labels['DeviceSettingsPage.parallelControl'],
                  key: 'Parallel_Control',
                  code: InverterSettingsCodeV3.ParallelControl,
                  field: {
                    hide: this.command?.validity.Parallel_Control === false,
                    value: data?.Parallel_Control,
                    constraint: {
                      options: [
                        { label: this.labels['Common.disable'], value: 0 },
                        { label: this.labels['Common.enable'], value: 1 },
                        ]},
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.parallelMasterSlave'],
                  key: 'Parallel_Master_Slave',
                  code: InverterSettingsCodeV3.ParallelMasterSlave,
                  field: {
                    hide: data?.Parallel_Control === 0 || this.command?.validity.Parallel_Master_Slave === false,
                    value: data?.Parallel_Master_Slave,
                    constraint: { options: [{ label: 'Slave', value: 0 }, { label: 'Master', value: 1 }] },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.parallelAddress'],
                  key: 'Parallel_Address',
                  code: InverterSettingsCodeV3.ParallelAddress,
                  field: {
                    hide: data?.Parallel_Control === 0 || this.command?.validity.Parallel_Address === false,
                    value: data?.Parallel_Address,
                    constraint: { options: Array.from({ length: 11 }, (__, i) => ({ label: i.toString(), value: i })) },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const inputModeSettings = {
        [this.labels['DeviceSettingsPage.inputModeSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.inputModeConfig$.pipe(
              map((data) => ({
                ...data,
                SN: this.cHandler.channel.inverterId,
              })),
              map((data) => ({
                component: this.inputChannelSelector!,
                context: {
                  data,
                  changeFn:(value: string) => this.execute.call(this, {
                    code: InverterSettingsCodeV3.PVInputModeSelection,
                    value,
                    key: '',
                  }),
                },
              })),
            ),
          },
        },
      };

      const workModeSettings = {
        [this.labels['DeviceSettingsPage.workModeSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.inputWorkModeInfo$.pipe(
              map((data) => [
                {
                  name: this.labels['DeviceSettingsPage.energyStorageModeControl'],
                  key: 'WORK_MODE',
                  code: InverterSettingsCodeV3.EnergyStorageModeControl,
                  field: {
                    hide: this.command?.validity.WORK_MODE === false,
                    value: data?.WORK_MODE,
                    constraint: {
                      options: [
                        { label: this.labels['DeviceSettingsPage.spontaneousSelfUseMode'], value: 0 },
                        {
                          label: this.labels['DeviceSettingsPage.timeOfUseElectricityPriceModel'],
                          value: 1,
                        },
                        {
                          label: this.labels['DeviceSettingsPage.timingChargingAndDischargingMode'],
                          value: 2,
                        },
                        { label: this.labels['DeviceSettingsPage.passiveMode'], value: 3 },
                        { label: this.labels['DeviceSettingsPage.peakClippingMode'], value: 4 },
                        { label: this.labels['DeviceSettingsPage.offGridMode'], value: 5 },
                        { label: this.labels['DeviceSettingsPage.generatorMode'], value: 6 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingID'],
                  key: 'TIMING_ID',
                  code: InverterSettingsCodeV3.TimingId,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_ID === false,
                    value: data?.TIMING_ID,
                    constraint: { options: Array.from({ length: 4 }, (__, i) => ({ label: i.toString(), value: i })) },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingOffOnControl'],
                  key: 'TIMING_OFF_ON_CONTROL',
                  code: InverterSettingsCodeV3.TimingOffOnControl,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_OFF_ON_CONTROL === false,
                    value: data?.TIMING_OFF_ON_CONTROL,
                    constraint: { options: [{
                      label: this.labels['DeviceSettingsPage.disabledDischargingAndCharging'],
                      value: 0,
                    }, {
                      label: this.labels['DeviceSettingsPage.enabledOnlyCharging'],
                      value: 1,
                    }, {
                      label: this.labels['DeviceSettingsPage.enabledOnlyDischarging'],
                      value: 2,
                    }, {
                      label: this.labels['DeviceSettingsPage.enabledDischargingAndCharging'],
                      value: 3,
                    }] },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingChargeStart'],
                  key: 'TIMING_CHARGE_START',
                  code: InverterSettingsCodeV3.TimingChargeStart,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_CHARGE_START === false,
                    value: splitHighLow(data?.TIMING_CHARGE_START as number).join(','),
                    constraint: {
                      template: this.timeSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingChargeEnd'],
                  key: 'TIMING_CHARGE_END',
                  code: InverterSettingsCodeV3.TimingChargeEnd,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_CHARGE_END === false,
                    value: splitHighLow(data?.TIMING_CHARGE_END as number).join(','),
                    constraint: {
                      template: this.timeSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingDischargeStart'],
                  key: 'TIMING_DISCHARGE_START',
                  code: InverterSettingsCodeV3.TimingDischargeStart,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_DISCHARGE_START === false,
                    value: splitHighLow(data?.TIMING_DISCHARGE_START as number).join(','),
                    constraint: {
                      template: this.timeSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingDischargeEnd'],
                  key: 'TIMING_DISCHARGE_END',
                  code: InverterSettingsCodeV3.TimingDischargeEnd,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_DISCHARGE_END === false,
                    value: splitHighLow(data?.TIMING_DISCHARGE_END as number).join(','),
                    constraint: {
                      template: this.timeSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingChargePower'],
                  key: 'TIMING_CHARGE_POWER',
                  code: InverterSettingsCodeV3.TimingChargePower,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_CHARGE_POWER === false,
                    value: data?.TIMING_CHARGE_POWER,
                    constraint: { min: 0, max: 65535 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timingDischargePower'],
                  key: 'TIMING_DISCHARGE_POWER',
                  code: InverterSettingsCodeV3.TimingDischargePower,
                  field: {
                    hide: data?.WORK_MODE !== 2 || this.command?.validity.TIMING_DISCHARGE_POWER === false,
                    value: data?.TIMING_DISCHARGE_POWER,
                    constraint: { min: 0, max: 65535 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timeOfUseElectricityPriceRuleNumber'],
                  key: 'TOU_ID',
                  code: InverterSettingsCodeV3.TouId,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_ID === false,
                    value: data?.TOU_ID,
                    constraint: {
                      options: [
                        { label: 'Rule 0', value: 0 },
                        { label: 'Rule 1', value: 1 },
                        { label: 'Rule 2', value: 2 },
                        { label: 'Rule 3', value: 3 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timeOfUsePriceRuleEnable'],
                  key: 'TOU_On_Off_Control',
                  code: InverterSettingsCodeV3.TouOnOffControl,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_On_Off_Control === false,
                    value: data?.TOU_On_Off_Control,
                    constraint: { options: [{ label: 'Off', value: 0 }, { label: 'On', value: 1 }] },
                    type: 'select',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.chargingStartHourAndMinute'],
                  key: 'TOU_Charge_Start',
                  code: InverterSettingsCodeV3.TouChargeStart,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Charge_Start === false,
                    value: splitHighLow(data?.TOU_Charge_Start as number).join(','),
                    constraint: {
                      template: this.timeSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.chargingEndHourAndMinute'],
                  key: 'TOU_Charge_End',
                  code: InverterSettingsCodeV3.TouChargeEnd,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Charge_End === false,
                    value: splitHighLow(data?.TOU_Charge_End as number).join(','),
                    constraint: {
                      template: this.timeSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timeOfUsePriceMandatoryChargingCutOffSOC'],
                  key: 'TOU_Charge_Target_SOC',
                  code: InverterSettingsCodeV3.TouChargeTargetSoc,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Charge_Target_SOC === false,
                    value: data?.TOU_Charge_Target_SOC,
                    constraint: { min: 0, max: 100 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timeOfUsePriceMandatoryChargingPower'],
                  key: 'TOU_Charge_Power',
                  code: InverterSettingsCodeV3.TouChargePower,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Charge_Power === false,
                    value: data?.TOU_Charge_Power,
                    constraint: { min: 0, max: 4294967295 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.theMonthAndDateWhenTheRuleBecomesEffective'],
                  key: 'TOU_Executed_Date_Start',
                  code: InverterSettingsCodeV3.TouExecutedDateStart,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Executed_Date_Start === false,
                    value: splitHighLow(data?.TOU_Executed_Date_Start as number).join(','),
                    constraint: {
                      template: this.dateSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.theMonthAndDateWhenTheRuleCeasesToTakeEffect'],
                  key: 'TOU_Executed_Date_End',
                  code: InverterSettingsCodeV3.TouExecutedDateEnd,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Executed_Date_End === false,
                    value: splitHighLow(data?.TOU_Executed_Date_End as number).join(','),
                    constraint: {
                      template: this.dateSelector,
                    },
                    type: 'custom',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.timeOfUseElectricityPriceWeekWhenTheRuleTakesEffect'],
                  key: 'TOU_Executed_Day_of_Week',
                  code: InverterSettingsCodeV3.TouExecutedDayOfWeek,
                  field: {
                    hide: data?.WORK_MODE !== 1 || this.command?.validity.TOU_Executed_Day_of_Week === false,
                    value: ((data?.TOU_Executed_Day_of_Week as number) ?? 0).toString(2).padStart(7, '0').split('').reverse().join(''),
                    type: 'custom',
                    constraint: {
                      template: this.weekdaySelector,
                    },
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.batteryPowerLowerLimit'],
                  key: 'Passive_Manual_Blo',
                  code: InverterSettingsCodeV3.PassiveManualBlo,
                  field: {
                    hide: data?.WORK_MODE !== 3 || this.command?.validity.Passive_Manual_Blo === false,
                    value: data?.Passive_Manual_Blo,
                    constraint: { min: -2147483648, max: 2147483647 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.batteryPowerUpperLimit'],
                  key: 'Passive_Manual_Bup',
                  code: InverterSettingsCodeV3.PassiveManualBup,
                  field: {
                    hide: data?.WORK_MODE !== 3 || this.command?.validity.Passive_Manual_Bup === false,
                    value: data?.Passive_Manual_Bup,
                    constraint: { min: -2147483648, max: 2147483647 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.desireGridPower'],
                  key: 'Passive_Manual_Gdes',
                  code: InverterSettingsCodeV3.PassiveManualGdes,
                  field: {
                    hide: data?.WORK_MODE !== 3 || this.command?.validity.Passive_Manual_Gdes === false,
                    value: data?.Passive_Manual_Gdes,
                    constraint: { min: -2147483648, max: 2147483647 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
              ]),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      const purchasePowerLimitSettings = {
        [this.labels['DeviceSettingsPage.purchasePowerLimitSettings']]: {
          flow: {
            type: 'component' as const,
            value$: this.purchasePowerLimitConfig$.pipe(
              map((data) => ([
                {
                  name: this.labels['DeviceSettingsPage.purchasePowerLimitControl'],
                  key: 'Purchase_Total_Power_Limit_Enable',
                  code: InverterSettingsCodeV3.PurchasePowerLimitEnable,
                  field: {
                    hide: data?.Purchase_Total_Power_Limit_Enable === undefined,
                    value: data?.Purchase_Total_Power_Limit_Enable,
                    constraint: {
                      options: [
                        { label: this.labels['Common.disable'], value: 0 },
                        { label: this.labels['Common.enable'], value: 1 },
                      ],
                    },
                    type: 'select',
                    submit: this.labels['Common.change'],
                  },
                },
                {
                  name: this.labels['DeviceSettingsPage.purchasePowerLimitValue'],
                  key: 'Purchase_Total_Power_Limit_Value',
                  code: InverterSettingsCodeV3.PurchasePowerLimitValue,
                  field: {
                    hide: data?.Purchase_Total_Power_Limit_Enable === undefined,
                    value: (data?.Purchase_Total_Power_Limit_Value as number),
                    constraint: { min: 3000, max: 65535 },
                    type: 'input',
                    inputType: 'number',
                    submit: this.labels['Common.save'],
                  },
                },
              ])),
              map((data) => ({
                component: this.content!,
                context: { data },
              })),
            ),
          },
        },
      };

      let settingList: DeviceModbusInfo[] = [
        ivCurveScanSettings,
        modbusConfig,
        inputModeSettings,
        antiRefluxSettings,
      ];

      if (this.getInverterModelType() === InverterModelType.HybridOrStorage) {
        settingList = [
          ...settingList,
          batteryConfig,
          epsSettings,
          batteryActiveConfig,
          unbalancedSupportConfig,
          parallelConfig,
          workModeSettings,
          purchasePowerLimitSettings,
        ];
      }

      return settingList;
    }),
  );

  constructor(
    private readonly channelHandler: ChannelHandler,
    private readonly errorSink: ErrorSinkService,
    private readonly insertDeviceCommandLogMutation: InsertDeviceCommandLogMutation,
    private readonly modbusRead: ModbusReadService,
    private readonly toastService: ToastService,
    private readonly uiPromiseService: UiPromiseService,
  ) {
    this.logConfigurationParameter = this.logConfigurationParameter.bind(this);
  }

  async ngOnInit() {

    const internetInput: TypeInternetDevice = {
      serial: this.inverterId,
      gatewaySN: this.gatewaySN,
      deviceType: 'inverter',
      modbusAddress: this.modbusAddress,
      onFatalError: () => this.fatalError.emit(),
    };

    const channel = (): CommunicationChannel => this.channelHandler.handler.channel;

    if (this.channelType) {
      // Explicit channel (e.g. AH) — NO fallback, error if it fails
      this.channelHandler.switch(this.channelType);
      await channel().open(internetInput);
    } else {
      // Original behaviour: MQTT → ETH fallback
      try {
        this.channelHandler.switch(CommunicationChannelType.InternetMQTT_ZCS);
        await channel().open(internetInput);
      } catch (error) {
        this.channelHandler.switch(CommunicationChannelType.InternetETHLongPolling_ZCS);
        await channel().open(internetInput);
      }
    }

    this.command = this.channelHandler.handler.channel.command;
    this.cHandler = this.channelHandler.handler;
    this.cHandler.channel.interaction = this.cHandler.channel.interaction.bind(this.cHandler.channel);
    const executionImplementation = this.execute.bind(this);
    this.execute = (...arg) => this.uiPromiseService.run(() => executionImplementation(...arg), {
      loadingMessage: this.labels['Common.saving'],
      loading: true,
    })
    .then((response) => {
      if (response.error) {
        this.toastService.persistence(this.labels['Common.saveFailed']);
      }
      this.refresh$.next();
    });
    this.execute = this.execute.bind(this);
    this.uiPromiseService.run = this.uiPromiseService.run.bind(this.uiPromiseService);

    this.isCommunicationReady$.next(true);
  }

  async ngOnDestroy() {
    await this.channelHandler.handler.channel.close();
  }

  async logConfigurationParameter({ code, value, key }: {
    code: InverterSettingsCodeV3 | InverterSettingsCodeV2 | InverterSettingsCodeV4;
    value: string | number;
    key: string;
  }) {
    const insertQuery = gql`
      fragment responsestatustype on ResponseStatusType {
        success
        statusCode
        message
      }
    `;

    const device_sn = this.cHandler.channel.inverterId;
    const timestamp = Math.floor(Date.now() / 1000);
    const param_name = key;
    const param_value = value.toString();
    const gateway_sn = `${this.cHandler.channel.loggerId}`;
    const string_log = `${param_name}=${param_value} (code=${code})`;
    const category = 'Configuration';

    Sentry.captureEvent({
      message: `Config Settings - ${device_sn}`,
      level: "info",
      tags: {
        operation: "parameter-configuration",
        device_sn,
        gateway_sn,
      },
      extra: {
        param_name,
        param_value,
        code,
        category,
        string_log,
        timestamp,
      },
    });

    this.insertDeviceCommandLogMutation.execute(insertQuery, {
      input: { device_sn, timestamp, param_name, param_value, gateway_sn, string_log, category }
    });
  }

  async execute({ code, value, key }: {
    code: InverterSettingsCodeV3 | InverterSettingsCodeV2 | InverterSettingsCodeV4;
    value?: string | number | null;
    key: string;
  }) {
    if (value === undefined || value === null) {
      return;
    }

    this.logConfigurationParameter({ code, value, key });

    switch (code) {
      case InverterSettingsCodeV3.BatteryActiveOneShot:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key: 'BatteryActive_Control',
            value: 1,
          }, {
            key: 'BatteryActive_Oneshot',
            value: 1,
          },
        ],
          V3BatteryActiveSettings,
        );
        break;
      case InverterSettingsCodeV3.Language:
        await this.command?.executeMixedRequests([
          {
            mode: 'write',
            key,
            value,
            table: V3GlobalSettings,
          },
        ]);
        break;
      case InverterSettingsCodeV3.BatteryConfigID:
        await this.command?.executeMixedRequests([
          {
            mode: 'write',
            key,
            value,
            table: V3BatterySettings,
          },
        ]);
        break;
        case InverterSettingsCodeV3.BatteryCurrentChargeLimit:
        case InverterSettingsCodeV3.BatteryCurrentDischargeLimit:
          await this.command?.executeWriteMultipleHoldingRegisters([{
              key,
              value: (value as number) * 100,
            }, {
              key: 'BAT_CONFIG_CONTROL',
              value: 1,
            },
          ],
          V3BatterySettings,
          {
            useValidityMask: 'AddressMask_Config_Basic2',
          },
          );
          break;
      case InverterSettingsCodeV3.BatteryCommunicationAddress:
        const data = this.cHandler.channel.command?.data;
        const batteryValue = setBatteryConfigAddress(value, data?.BAT_CONFIG_ID, data?.BAT_CONFIG_PROTOCOL);
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value: batteryValue,
          }, {
            key: 'BAT_CONFIG_CONTROL',
            value: 1,
          },
        ],
        V3BatterySettings,
          {
            useValidityMask: 'AddressMask_Config_Basic2',
          },
        );
        break;
      case InverterSettingsCodeV3.BatteryCommunicationProtocol:
      case InverterSettingsCodeV3.BatteryDepthOfDischarge:
      case InverterSettingsCodeV3.BatteryEndOfDischarge:
      case InverterSettingsCodeV3.BatteryConfigurationControl:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          }, {
            key: 'BAT_CONFIG_CONTROL',
            value: 1,
          },
        ],
        V3BatterySettings,
        {
          useValidityMask: 'AddressMask_Config_Basic2',
        },
        );
        break;
      case InverterSettingsCodeV3.PVInputModeSelection:
        const content = JSON.parse(value as string);
        if (!content || !content.address || !content.registers || !this.command) {
          break;
        }

        const request = this.command?.writeHoldingRegistersStandard(content.address, new Uint8Array(content.registers));
        this.command?.executeRawOperation(request);
        break;
      case InverterSettingsCodeV3.AntiRefluxControl:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
        ],
          V3AntiRefluxSettings,
        );
        break;
      case InverterSettingsCodeV3.AntiRefluxPower:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value: Math.floor((value as number) / 100),
          },
        ],
          V3AntiRefluxSettings,
        );
        break;
      case InverterSettingsCodeV3.IVCurveScanControl:
      case InverterSettingsCodeV3.IVCurveScanPeriod:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
        ],
          V3IVCurveScanSettings,
        );
        break;
      case InverterSettingsCodeV3.EpsControl:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
        ],
          V3EpsSettings,
        );
        break;
      case InverterSettingsCodeV3.BatteryActiveControl:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          }, {
            key: 'BatteryActive_Oneshot',
            value: 1,
          },
        ],
          V3BatteryActiveSettings,
        );
        break;
      case InverterSettingsCodeV3.UnbalancedSupportControl:
        await this.command?.executeMixedRequests([
          {
            mode: 'write',
            key,
            value,
            table: V3UnbalancedSupportSettings,
          },
        ]);
        break;
      case InverterSettingsCodeV3.ParallelControl:
      case InverterSettingsCodeV3.ParallelMasterSlave:
      case InverterSettingsCodeV3.ParallelAddress:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
        ],
          V3ParallelSettings,
        );
        break;
      case InverterSettingsCodeV3.PCCSampleMode:
        await this.command?.executeMixedRequests([
          {
            mode: 'write',
            key,
            value,
            table: V3DevicePCCSettings,
          },
        ]);
        break;
      case InverterSettingsCodeV3.EnergyStorageModeControl:
      case InverterSettingsCodeV3.TouId:
      case InverterSettingsCodeV3.TimingId:
        await this.command?.executeMixedRequests([
          {
            mode: 'write',
            key,
            value,
            table: V3DeviceWorkModeConfig,
          },
        ]);
        break;
      case InverterSettingsCodeV3.TouOnOffControl:
      case InverterSettingsCodeV3.TouChargeTargetSoc:
      case InverterSettingsCodeV3.TouChargePower:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
          {
            key: 'TOU_Control',
            value: 1,
          },
        ],
          V3DeviceWorkModeTOU,
        );
        break;
      case InverterSettingsCodeV3.TouChargeStart:
      case InverterSettingsCodeV3.TouChargeEnd:
      case InverterSettingsCodeV3.TouExecutedDateStart:
      case InverterSettingsCodeV3.TouExecutedDateEnd:
        const [touHigh, touLow] = (value as string).split(',').map((v) => parseInt(v, 10));
        value = mergeHighLow(touHigh, touLow);
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
          {
            key: 'TOU_Control',
            value: 1,
          },
        ],
          V3DeviceWorkModeTOU,
        );
        break;
      case InverterSettingsCodeV3.TouExecutedDayOfWeek:
        value = parseInt((value as string).split('').reverse().join(''), 2);
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
          {
            key: 'TOU_Control',
            value: 1,
          },
        ],
          V3DeviceWorkModeTOU,
        );
        break;

      case InverterSettingsCodeV3.TimingOffOnControl:
      case InverterSettingsCodeV3.TimingChargePower:
      case InverterSettingsCodeV3.TimingDischargePower:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
          {
            key: 'TIMING_CONTROL',
            value: 1,
          },
        ],
          V3DeviceWorkModeTiming,
        );
        break;

      case InverterSettingsCodeV3.TimingDischargeEnd:
      case InverterSettingsCodeV3.TimingDischargeStart:
      case InverterSettingsCodeV3.TimingChargeEnd:
      case InverterSettingsCodeV3.TimingChargeStart:
        const [Thigh, Tlow] = (value as string).split(',').map((v) => parseInt(v, 10));
        value = mergeHighLow(Thigh, Tlow);
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
          {
            key: 'TIMING_CONTROL',
            value: 1,
          },
        ],
          V3DeviceWorkModeTiming,
        );
        break;
      case InverterSettingsCodeV3.PassiveManualBlo:
      case InverterSettingsCodeV3.PassiveManualBup:
      case InverterSettingsCodeV3.PassiveManualGdes:
        await this.command?.executeWriteMultipleHoldingRegisters([{
          key,
          value,
        },
        ],
          V3DeviceWorkModePassive,
        );
        break;
      case InverterSettingsCodeV3.RS485ConfigAddress:
      case InverterSettingsCodeV3.RS485ConfigBaud:
      case InverterSettingsCodeV3.RS485ConfigParityBit:
      case InverterSettingsCodeV3.RS485ConfigStopBit:
        await this.command?.executeWriteMultipleHoldingRegisters([{
            key,
            value,
          },
          {
            key: 'RS485Config_Control',
            value: 1,
          },
        ],
          V3ModbusSettings,
          {
            useValidityMask: 'AddressMask_Config_Basic1',
          },
        );
        break;

      case InverterSettingsCodeV3.PurchasePowerLimitEnable:
      case InverterSettingsCodeV3.PurchasePowerLimitValue:
        const currentPurchaseData = this.cHandler.channel.command?.data;
        // User input is in Watts, register uses 0.1kW units (divide by 100)
        const powerPurchaseValue = key === 'Purchase_Total_Power_Limit_Value'
          ? Math.floor((value as number) / 100)
          : Math.floor((currentPurchaseData?.Purchase_Total_Power_Limit_Value as number) / 100) ?? 0;
        const enablePurchaseValue = key === 'Purchase_Total_Power_Limit_Enable'
          ? value as number
          : (currentPurchaseData?.Purchase_Total_Power_Limit_Enable as number) ?? 0;

        await this.command?.executeWriteMultipleHoldingRegisters([
          {
            key: 'Purchase_Total_Power_Limit_Enable',
            value: enablePurchaseValue,
          },
          {
            key: 'Purchase_Total_Power_Limit_Value',
            value: powerPurchaseValue,
          },
        ], V3PurchasePowerLimitSettings);
        break;

      default:
        throw new Error(`Unsupported InverterSettingsCodeV3: ${code}`);
    }
  }
}
